import type {HotelDetails} from '@/api/hoteldetails';
import type {State} from '@/redux/typesNew';
import type {TourOperatorSpecials} from '@/redux/v1/hotels/list/models';
import type {Price, Sponsored} from '@/redux/v1/models';

export const DEVICE_TYPE_MOBILE = 'm' as const;
export const DEVICE_TYPE_TABLET = 't' as const;
export const DEVICE_TYPE_DESKTOP = 'd' as const;

export type DeviceType =
  | typeof DEVICE_TYPE_MOBILE
  | typeof DEVICE_TYPE_TABLET
  | typeof DEVICE_TYPE_DESKTOP;

export const SCREEN_ORIENTATION_PORTRAIT = 'portrait' as const;
export const SCREEN_ORIENTATION_LANDSCAPE = 'landscape' as const;

export type ScreenOrientation =
  | typeof SCREEN_ORIENTATION_PORTRAIT
  | typeof SCREEN_ORIENTATION_LANDSCAPE;

export type BestOfferTypeName =
  | 'best value'
  | 'best price'
  | 'premium'
  | 'sponsored'
  | 'no best offer';

export type HotelListItem = {
  id: string;
  price: Price;
  position: number;
  specials?: TourOperatorSpecials;
  data?: HotelDetails;
  sponsored?: Sponsored;
  ratingScore: number | null;
};

export type HotelListEcommerceItem = {
  item_name: string;
  item_id: string;
  price: number;
  currency: string;
  item_list_name: string;
  item_category: string;
  item_category2: string;
  item_category4: string;
  item_brand: string;
  index: number;
  location_id: string;
  quantity: 1;
  item_sustainability: '0' | '1';
};

type RecoInfo = {
  pricesSkipped: boolean;
  placementId: string;
  modelId: string;
  pricesTime: number;
  strategy: string;
  ptoken: string;
};

export type Recommendation = {
  hotelId: string;
  hotelName: string;
  title: string;
  category3: string;
  list_name: string;
  formattedPrice: number;
  pax: number;
  price: number;
  index: number;
  quantity: number;
  recoInfo: RecoInfo;
};

export type BD4TravelEcommerceItem = {
  item_id: string;
  item_name: string;
  item_title: string;
  item_category3: string;
  item_list_name: string;
  price: number;
  index: number;
  quantity: number;
};

type BD4TravelPrices = {
  total: number;
  pp: number;
};

export type BD4TravelElement = {
  id: string;
  prices: BD4TravelPrices;
};

export type ProductEcommerceItem = {
  item_altflight_price: number;
  item_upselling_price: number;
  item_name: string;
  item_id: string;
  price: number;
  currency: string;
  item_list_name: string;
  item_category: string;
  item_category2: string;
  item_category4: string;
  item_brand: string;
  index?: number;
  location_id: string;
  quantity: 1;
  item_category3: string;
  item_category5: string;
  item_variant: string;
  item_bestoffer_type: BestOfferTypeName;
  item_sustainability: string;
  item_upselling_type: string;
};

export type FilterParams = {
  search_board?: string;
  search_room?: string;
  search_hotel_category?: string;
  search_user_rating?: string;
  search_duration?: number | string;
  search_pool?: string;
  search_beach?: string;
  search_wellness?: string;
  search_children_offers?: string;
  search_entertainment?: string;
  search_sport?: string;
  search_watersport?: string;
  search_restaurant?: string;
  search_hotel_facilities?: string;
  search_hotel_type?: string;
  search_price_max?: number | string;
  search_price_min?: number | string;
  search_seaview?: string;
  search_room_attributes?: string;
  search_organizer?: string;
  search_arrival_departure_time_min?: string;
  search_arrival_departure_time_max?: string;
  search_departure_departure_time_min?: string;
  search_departure_departure_time_max?: string;
  search_transfer?: string;
  search_organizer_promotion?: string;
  search_flex_option?: string;
  search_safety_option?: string;
  search_sustainability?: string;
  search_airlines?: string;
  search_destination_airports?: string;
};

export type OfferListEventActionType =
  | 'offer_list_loaded'
  | 'offer_list_lazyload'
  | 'sort_offer_list'
  | 'sort_offer_list_lazyload'
  | 'filter_offer_list'
  | 'filter_offer_list_lazyload';

export type ItemListName =
  | 'offer list - longest duration'
  | 'offer list - lowest price'
  | 'offer list - earliest arrival'
  | 'hotel list - longest duration'
  | 'hotel list - lowest price'
  | 'hotel list - earliest arrival';

export type EventHandler<Action, Event> = (
  action: Action,
  previousState: State,
  nextState?: State
) => Event;

export type EventAction<Payload> = {
  type: string;
  payload: Payload;
};

export type EventActionCreator<Action, Payload> = (payload: Payload) => Action;

export type OfferEcommerceItem = {
  item_name: string;
  item_id: string;
  price: number;
  currency: string;
  item_list_name?: string;
  item_category: string;
  item_category2: string;
  item_category3: string;
  item_category4: string;
  item_category5: string;
  item_brand: string;
  item_sustainability: string;
  item_variant: string;
  index?: number;
  location_id: string;
  quantity: 1;
  item_bestoffer_type: BestOfferTypeName;
};

type EcommerceNullEvent<
  Event extends {
    event: string;
    event_action: string;
  },
> = [{ecommerce: null}, Event];

type P195Impression = {
  event: 'impression';
  event_action: `${string}_sponsored_ad_loaded` | 'ad_loaded';
  ad_url: string;
  ad_position: string;
  giata_id: string;
};

type P195ImpressionViewable = {
  event: 'impression';
  event_action: `${string}_sponsored_ad_viewable` | 'ad_viewable';
  ad_url: string;
  ad_position: string;
  giata_id: string;
};

type ServiceCenterBannerHotlineClicked = {
  event: 'click';
  event_action: 'phone_numbers_click';
  non_interaction: false;
};

type ServiceCenterBannerCallbackButtonClicked = {
  event: 'click';
  event_action: 'arrange_callback';
  non_interaction: false;
};

type UpsellingOffersLoaded = {
  event: 'upselling';
  event_action: 'vacancy_upselling_products';
  number_of_up_products: number;
};

type UsellingOfferSelected = {
  event: 'upselling';
  event_action: 'click_upselling_product';
  upselling_type: Array<string>;
  upselling_price: number;
};

type AlternativeFlightSelected = {
  event: 'altflights';
  event_action: 'select_alternative_flight';
  upselling_type: Array<string>;
  altflights_price: number;
};

type ExitlayerClosed = {
  event: 'find';
  event_action: 'exit_layer_closed';
};

type ExitlayerLoaded = {
  event: 'find';
  event_action: 'exit_layer_loaded';
};

type ExitlayerLeipzigClosed = {
  event: 'find';
  event_action: `exit_layer_closed - ${string}`;
  non_interaction: false;
};

type ExitlayerLeipzigLoaded = {
  event: 'find';
  event_action: `exit_layer_loaded - ${string}`;
  non_interaction: true;
};

type ExitlayerLeipzigCTAClick = {
  event: 'find';
  event_action: `exit_layer_button_click - ${string}`;
  non_interaction: false;
};

type ExitlayerLeipzigIt2Closed = {
  event: 'find';
  event_action: `exit_layer_leipzig_it2_closed - ${string}`;
  non_interaction: false;
};

type ExitlayerLeipzigIt2Loaded = {
  event: 'find';
  event_action: `exit_layer_leipzig_it2_loaded - ${string}`;
  non_interaction: true;
};
type ExitlayerLeipzigIt2CTAClick = {
  event: 'find';
  event_action: `exit_layer_leipzig_it2_button_click - ${string}`;
  non_interaction: false;
};

type ExitlayerIt2Closed = {
  event: 'find';
  event_action: `exit_layer_it2_closed - ${string}`;
  non_interaction: false;
};
type ExitlayerIt2Loaded = {
  event: 'find';
  event_action: `exit_layer_it2_loaded - ${string}`;
  non_interaction: true;
};
type ExitlayerIt2CTAClick = {
  event: 'find';
  event_action: `exit_layer_it2_button_click - ${string}`;
  non_interaction: false;
};

type OfferCollapsed = {
  event: 'click';
  event_action: 'collapse_offer_click';
};

type OfferClosed = {
  event: 'click';
  event_action: 'close_offer_click';
};

type UpsellingOffersLoadMoreClicked = {
  event: 'upselling';
  event_action: 'click_upselling_more_offers';
};

type AlternativeFlightLoadMoreClicked = {
  event: 'altflights';
  event_action: 'click_altflights_more_offers';
};

type AlternativeFlightLoaded = {
  event: 'altflights';
  event_action: 'vacancy_altflights_products';
  number_of_altflight_products: number;
};

type NoOffer = {
  event: 'nooffer';
  event_action: 'nooffer';
};

type BD4RecommendedNotLoad = {
  event: 'reconoview';
  event_action: 'BD4_recommender_not_loaded';
  listId: string;
};

type InterstitialBannerLoaded = EcommerceNullEvent<{
  event: 'view_promotion';
  event_action: 'promotion_banner_loaded_interstitial_ad';
  non_interaction: true;
  ecommerce: {
    promotion_name: 'Interstitial Ad';
  };
}>;

type InterstitialBannerClicked = EcommerceNullEvent<{
  event: 'select_promotion';
  event_action: 'promotion_banner_click_interstitial_ad';
  non_interaction: false;
  ecommerce: {
    promotion_name: 'Interstitial Ad';
  };
}>;

type InterstitialBannerClosed = {
  event: 'click';
  event_action: 'promotion_banner_closed_interstitial_ad';
  non_interaction: false;
};

type InactivityDetect =
  | {
      event: 'gaNonEvent';
      eventCategory: 'Inactivity Banner';
      eventAction: 'Impression';
    }
  | {
      event: 'gaEvent';
      eventCategory: 'Inactivity Banner';
      eventAction: 'Angebote aktualisieren';
    };

type MapViewClick = {
  event: 'click';
  event_action: 'map_clicked';
  non_interaction: false;
};

type MapViewClickBack = {
  event: 'click';
  event_action: 'back_to_hotellist_clicked';
  non_interaction: false;
};

type PriceChartToggled = {
  event: 'click';
  event_action: 'pricechart_collapsed' | 'pricechart_uncollapsed';
  non_interaction: false;
};

type PriceChartReset = {
  event: 'filter';
  event_action: 'filter_set_offer_list';
  search_startdate: string;
  non_interaction: false;
};

type PriceChartNavigated = {
  event: 'click';
  event_action: `pricechart_slider_${'next' | 'previous'}`;
  non_interaction: false;
};

type PriceChartLoaded = {
  event: 'impression';
  event_action: 'pricechart';
  non_interaction: true;
};

type PriceChartClick = {
  event: 'filter';
  event_action: 'set_filter_pricechart';
  search_startdate: string;
  non_interaction: false;
};

type TourOperatorDiscountBannerClicked = {
  event: 'select_promotion';
  event_action: 'promotion_banner_click_last_minute';
  non_interaction: false;
  ecommerce: {
    promotion_name: 'Last Minute';
    creative_name: string;
  };
};

type TourOperatorDiscountBannerLoaded = {
  event: 'view_promotion';
  event_action: 'promotion_banner_loaded_last_minute';
  non_interaction: true;
  ecommerce: {
    promotion_name: 'Last Minute';
    creative_name: string;
  };
};

type DuplicateHotelCount = {
  event: 'find';
  event_action: 'duplicate_hotel_count';
  duplicate_hotel_count: number;
  non_interaction: true;
};

type LoadMoreHotels = {
  event: 'click';
  event_action: 'load_more_hotels';
  list_position: number;
  non_interaction: false;
};

type RegionFilter = {
  event: 'filter';
  event_action: 'region_filter_set';
  region_filter_input: Array<string>;
};

type OfferListGalleryCtaButtonClicked = {
  event: 'gallery';
  event_action: 'click_to_offer';
};

type GalleryEvent = {
  event: 'gallery';
  event_action:
    | 'gallery_details_click'
    | 'close_gallery_details'
    | `gallery_slider_${'next' | 'previous'}`;
  non_interaction: false;
};

type HoneepotOpenExitIntent = {
  event: 'chatbot';
  event_action: 'honeepot_exit_intent';
  non_interaction: true;
};

type HoneepotOpenClick = {
  event: 'chatbot';
  event_action: 'honeepot_click';
  non_interaction: false;
};

type CrossBordersBannerLoad = {
  event: 'cross_border';
  event_action: 'banner_shown';
  non_interaction: true;
  crb_bannertype: 'button_with_price' | 'button_without_price';
};

type CrossBordersBannerClick = {
  event: 'cross_border';
  event_action: 'banner_clicked';
  non_interaction: false;
  crb_bannertype: 'button_with_price' | 'button_without_price';
};

type PromotionBannerVariants = 'cashback';

type PromotionBannerClick = {
  event: 'promotion_banner';
  event_action: `${PromotionBannerVariants}_click`;
  ad_position: `${number}`;
  non_interaction: false;
};

type PromotionBannerLoaded = {
  event: 'promotion_banner';
  event_action: `${PromotionBannerVariants}_loaded`;
  ad_position: `${number}`;
  non_interaction: true;
};

type WishlistAddSuccess = EcommerceNullEvent<{
  event: 'add_to_wishlist';
  event_action: 'hotel_added_to_wishlist';
  non_interaction: false;
  ecommerce: {
    currency: string;
    value: number;
    items: [
      {
        currency: string;
        index: number;
        item_brand: string;
        item_category: string;
        item_category2: string;
        item_category4: string;
        item_id: string;
        item_list_name: string;
        item_name: string;
        item_sustainability: '1' | '0';
        price: number;
        quantity: 1;
      },
    ];
  };
}>;

export type HotelListLoadedEventAction =
  | 'hotel_list_loaded'
  | 'hotel_list_lazyload'
  | 'search_hotel_list'
  | 'search_hotel_list_lazyload'
  | 'filter_hotel_list'
  | 'filter_hotel_list_lazyload'
  | 'sort_hotel_list'
  | 'sort_hotel_list_lazyload';

export type HotelListLoadedEvent = EcommerceNullEvent<
  {
    event: 'view_item_list';
    event_action: HotelListLoadedEventAction;
    non_interaction: boolean;
    hotel_list_offset: number;
    term: string;
    ecommerce: {
      items: Array<any>;
    };
  } & FilterParams
>;

export type OfferListLoadedEventAction =
  | 'offer_list_loaded'
  | 'offer_list_lazyload'
  | 'filter_offer_list'
  | 'filter_offer_list_lazyload'
  | 'sort_offer_list'
  | 'sort_offer_list_lazyload'
  | 'search_offer_list'
  | 'search_offer_list_lazyload';

export type OfferListLoadedEvent = EcommerceNullEvent<{
  event: 'view_item_list';
  event_action: OfferListLoadedEventAction;
  non_interaction: boolean;
  offer_list_offset: number;
  ecommerce: {
    items: Array<OfferEcommerceItem>;
  };
}>;

type TrackingEventData =
  // P195
  | P195Impression
  | P195ImpressionViewable
  // Service Center Banner
  | ServiceCenterBannerHotlineClicked
  | ServiceCenterBannerCallbackButtonClicked
  // Upselling
  | UpsellingOffersLoaded
  | UsellingOfferSelected
  | UpsellingOffersLoadMoreClicked
  //  Alternative Flights
  | AlternativeFlightLoaded
  | AlternativeFlightSelected
  | AlternativeFlightLoadMoreClicked
  // Exit Layer
  | ExitlayerClosed
  | ExitlayerLoaded
  | ExitlayerLeipzigClosed
  | ExitlayerLeipzigLoaded
  | ExitlayerLeipzigCTAClick
  | ExitlayerLeipzigIt2Closed
  | ExitlayerLeipzigIt2Loaded
  | ExitlayerLeipzigIt2CTAClick
  | ExitlayerIt2Closed
  | ExitlayerIt2Loaded
  | ExitlayerIt2CTAClick

  // No Offer
  | NoOffer
  // BD4 Recommended
  | BD4RecommendedNotLoad
  // Interstitial Banner
  | InterstitialBannerLoaded
  | InterstitialBannerClicked
  | InterstitialBannerClosed
  | InactivityDetect
  // Map View
  | MapViewClickBack
  | MapViewClick
  // Price Chart
  | PriceChartToggled
  | PriceChartReset
  | PriceChartNavigated
  | PriceChartLoaded
  | PriceChartClick
  // Tour Operator Discount Banner
  | TourOperatorDiscountBannerClicked
  | TourOperatorDiscountBannerLoaded
  // Hotel List
  | DuplicateHotelCount
  | LoadMoreHotels
  | RegionFilter
  // Offer List
  | OfferListGalleryCtaButtonClicked
  | OfferCollapsed
  | OfferClosed
  // Picture Gallery
  | GalleryEvent
  // Honeepot
  | HoneepotOpenClick
  | HoneepotOpenExitIntent
  // Cross Borders Sales Banner
  | CrossBordersBannerLoad
  | CrossBordersBannerClick
  // Wishlist
  | WishlistAddSuccess
  // Hotel List
  | HotelListLoadedEvent
  // Offer List
  | OfferListLoadedEvent
  // Promotion Banner
  | PromotionBannerClick
  | PromotionBannerLoaded;

export type TrackingEvent = {
  type: 'TRACKING';
  payload: TrackingEventData;
};
